import { Button, Hero, HeroSearchControllerProps, ProductLink } from '@dfh-corp-sites/ui'
import { ChevronRight, WideRightArrow } from '@dfh-corp-sites/ui/icons'
import { graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { getGatsbyImageData } from 'gatsby-source-sanity/lib/images/getGatsbyImageProps'
import { FileText } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import rehype2react from 'rehype-react'
import markdown from 'remark-parse'
import remark2rehype from 'remark-rehype'
import unified from 'unified'

import clientConfig from '../../../../client-config'
import { SanityCta } from '../../../graphql/gatsby'
import {
  FullPredictiveSearchDocument,
  FullPredictiveSearchQuery,
  FullPredictiveSearchQueryVariables,
} from '../../../graphql/search'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import useMediaQuery from '../../../hooks/useMediaQuery'
import useOutsideClick from '../../../hooks/useOutsideClick'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { getResourceAssetUrl, hasResourceAssetPage, ResourceTypeSlug } from '../../../lib/library'
import { usePageUrl } from '../../../lib/pages'
import { useSanityRoleAndContextBasedContent } from '../../../lib/sanity'
import useSanityImage from '../../../lib/sanity/hooks/useSanityImage'
import { useSearchLazyQuery } from '../../../lib/search'
import { imageUrlFor } from '../../../utils/image-url'
import { libraryResourceIcon, resourceTypeUrlMapping } from '../../../utils/libraryTools'
import Link from '../../global/Link'
import SanityLink, { NormalizedSanityLinkTagged } from '../../global/SanityLink'
import ParentProductCard from '../../products/ParentProductCard'
import { ProductLinkType, SanityHeroFiltered } from './types'

const SearchProductCard: React.FC<{
  product: FullPredictiveSearchQuery['predictiveSearch']['parentProducts'][0]
}> = ({ product }) => {
  return <ParentProductCard key={product.id} view="list" product={product} />
}

const SearchResourceLink: React.FC<{
  resource: FullPredictiveSearchQuery['predictiveSearch']['libraryResources'][0]
}> = ({ resource }) => {
  const isMobile = useMediaQuery({ maxWidth: '768px' })
  const isTablet = useMediaQuery({ minWidth: '769px', maxWidth: '1024px' })
  const sanityImageUrl = useSanityImage(
    { sourceJson: resource?.thumbnailImage?.json ?? undefined },
    {
      dpr: 2,
      fit: 'max',
      // set width only to avoid crop bug: https://github.com/sanity-io/image-url/issues/32
      width: 250,
    },
  )

  const resourceTypeMapped = resourceTypeUrlMapping[resource.categorySlug as ResourceTypeSlug]
  const device = useMemo(() => {
    if (isMobile) return 'mobile'
    if (isTablet) return 'tablet'
    return 'desktop'
  }, [isMobile, isTablet])

  const getLibraryResourceIcon = () => {
    return resourceTypeMapped === 'socialMediaResource' && sanityImageUrl
      ? imageUrlFor(sanityImageUrl).width(120).height(120).dpr(2).auto('format').url()
      : libraryResourceIcon({ resourceType: resourceTypeMapped, device })
  }

  const image = sanityImageUrl || getLibraryResourceIcon()
  const target = hasResourceAssetPage({ _type: resourceTypeMapped }) ? '_self' : '_blank'

  return (
    <Link
      target={target}
      to={getResourceAssetUrl({
        _type: resourceTypeMapped,
        slug: {
          current: resource.slug,
        },
      })}
      className="border-b border-b-brand-rule-grey flex w-full gap-3"
    >
      <img src={image || ''} alt={resource.title} className="w-20 h-[100px]" />
      <div className="w-full grid md:grid-cols-[1fr_auto] justify-between grid-cols-1 gap-3">
        <div className="flex gap-2">
          <div className="flex flex-col justify-start">
            <span className="font-medium text-brand-navy-2 uppercase">{resource.categorySlug}</span>
            <p className="text-wrap md:mt-4 text-2xl md:text-[32px] font-normal line-clamp-1">
              {resource.title}
            </p>
          </div>
        </div>
        <Button variant="text" className="gap-3.5 justify-start">
          See Resource Details <ChevronRight size={16} />
        </Button>
      </div>
    </Link>
  )
}

const SearchPageLink: React.FC<{
  page: FullPredictiveSearchQuery['predictiveSearch']['pages'][0]
}> = ({ page }) => {
  return (
    <Link to={`/${page.slug}`} className="border-b border-b-brand-rule-grey flex w-full gap-3">
      <FileText className="w-[80px] h-[100px] stroke-brand-orange stroke-1" />
      <div className="w-full grid md:grid-cols-[1fr_auto] justify-between grid-cols-1 gap-3">
        <div className="flex gap-2">
          <div className="flex flex-col justify-start">
            <span className="font-medium text-brand-navy-2 uppercase">Page</span>
            <p className="text-wrap md:mt-4 text-2xl md:text-[32px] font-normal line-clamp-1">
              {page.title}
            </p>
          </div>
        </div>
        <Button variant="text" className="gap-3.5 justify-start">
          See Page Details <ChevronRight size={16} />
        </Button>
      </div>
    </Link>
  )
}

const HeroProductLink: React.FC<{ link: ProductLinkType; index: number }> = ({ link, index }) => {
  const sanityImageUrl = useSanityImage(
    { source: link.image?.asset?.url },
    {
      dpr: 2,
      fit: 'max',
      width: 96,
    },
  )

  return (
    <Link
      key={index}
      to={`/products?category=${encodeURIComponent(link.category || '')}`}
      className="w-full"
    >
      <ProductLink
        name={link.title}
        image={sanityImageUrl || undefined}
        position={`0${index + 1}`}
      />
    </Link>
  )
}

const pushAnalyticsEvents = ({
  data,
  query,
}: {
  data: FullPredictiveSearchQuery
  query: string
}) => {
  const { libraryResources, pages, parentProducts } = data.predictiveSearch
  const isEmpty = !libraryResources.length && !parentProducts.length && !pages.length
  if (isEmpty) {
    setAnalyticsEvent({
      event: 'search_no_results',
      search_term: query,
    })
  } else {
    setAnalyticsEvent({
      event: 'search',
      search_term: query,
      result_count: libraryResources.length + parentProducts.length + pages.length,
    })
  }
}

const searchDefaultResults = {
  parentProducts: [],
  libraryResources: [],
  pages: [],
  aggregations: {
    parentProductCategories: [],
  },
  searchTerms: [],
}

const markdownProcessor = unified()
  .use(markdown)
  .use(remark2rehype)
  .use(rehype2react, { createElement: React.createElement })

type PageSelection = FullPredictiveSearchQuery['predictiveSearch']['pages'][0]
type ProductSelection = FullPredictiveSearchQuery['predictiveSearch']['parentProducts'][0]
type ResourceSelection = FullPredictiveSearchQuery['predictiveSearch']['libraryResources'][0]

const HeroSearchInputController = ({
  InputAndResults,
}: HeroSearchControllerProps<
  PageSelection,
  ProductSelection,
  ResourceSelection
>): JSX.Element | null => {
  const { navigate } = usePageUrl()
  const [isOpen, setIsOpen] = useState(false)
  const { elementRef: outsideClickRef } = useOutsideClick<HTMLDivElement>(
    useCallback(() => {
      setIsOpen(false)
    }, [setIsOpen]),
  )

  const [executeAutoCompleteSearch, { data, loading, error, refetch, called }] = useSearchLazyQuery(
    FullPredictiveSearchDocument,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (searchData) => {
        if (searchData) {
          pushAnalyticsEvents({ data: searchData as FullPredictiveSearchQuery, query: keywords })
        }
      },
      variables: {
        query: '',
      },
    },
  )

  const [inputValue, setInputValue] = useState('')
  const keywords = (inputValue || '').trim()
  const results = data?.predictiveSearch ?? searchDefaultResults

  useLogAndCaptureError(error)

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOpen(true)
    setInputValue(event.target.value)
  }, [])

  const handleInputKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && inputValue.trim().length > 0) {
        navigate(`/search-results?keywords=${encodeURIComponent(inputValue)}`)
      }
    },
    [inputValue, navigate],
  )

  // Execute search when keywords change, but only for hero variant
  useEffect(() => {
    if (keywords) {
      const apiVariables: FullPredictiveSearchQueryVariables = {
        query: keywords,
      }
      executeAutoCompleteSearch({ variables: apiVariables })
    }
  }, [executeAutoCompleteSearch, keywords])

  useEffect(() => {
    if (keywords) {
      const apiVariables: FullPredictiveSearchQueryVariables = {
        query: keywords,
      }
      if (called && refetch) {
        refetch(apiVariables)
      } else {
        executeAutoCompleteSearch({ variables: apiVariables })
      }
    }
  }, [called, executeAutoCompleteSearch, refetch, keywords])

  const searchResults = {
    loading,
    categories: results.aggregations.parentProductCategories,
    products: results.parentProducts,
    libraryResources: results.libraryResources,
    pages: results.pages,
  }

  return (
    <InputAndResults
      inputValue={inputValue}
      inputWrapperRef={outsideClickRef}
      onInputChange={handleInputChange}
      onInputKeyUp={handleInputKeyUp}
      onInputFocus={() => setIsOpen(true)}
      searchResults={searchResults}
      showSearchResults={keywords.length > 0 && isOpen}
    />
  )
}

export interface SanityHeroProps extends SanityHeroFiltered {
  className?: string
}

export const SanityHero: React.FC<SanityHeroProps> = ({
  variant,
  headerLabel,
  titleEmphasis,
  titleRemainder,
  subtitle,
  content,
  primaryCta,
  secondaryCta,
  heroImage,
  imageAlt,
  primaryCtaAriaLabel,
  secondaryCtaAriaLabel,
  roleBasedContent,
  contextBasedContent,
  mediaType,
  mediaUrl,
  productLinks,
}) => {
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const shouldDisplay = useMemo(
    () => shouldDisplayContent({ contextBasedContent, roleBasedContent }),
    [contextBasedContent, roleBasedContent, shouldDisplayContent],
  )

  const { renderProductCard, renderResourceLink, renderPageLink } = useMemo(
    () => ({
      renderProductCard: (product: ProductSelection) => {
        return <SearchProductCard product={product} />
      },
      renderResourceLink: (resource: ResourceSelection) => {
        return <SearchResourceLink resource={resource} />
      },
      renderPageLink: (page: PageSelection) => {
        return <SearchPageLink page={page} />
      },
    }),
    [],
  )

  useEffect(() => {
    const heroKey = `${variant}-${headerLabel}-${titleEmphasis}-${titleRemainder}`
    // Only fire if shouldDisplay is true AND there are no primary or secondary CTAs
    const hasPrimaryCta = !!(primaryCta && primaryCta.link && primaryCta.link.target?.length)
    const hasSecondaryCta = !!(
      secondaryCta &&
      secondaryCta.link &&
      secondaryCta.link.target?.length
    )

    if (shouldDisplay && !hasPrimaryCta && !hasSecondaryCta) {
      setAnalyticsEvent({
        _key: heroKey,
        event: 'select_content',
        content_type: 'hero',
        item_id: `hero-${variant}`,
        title: headerLabel || '',
        subtitle: subtitle || '',
        label_text: '',
        module_name: 'SanityHero',
        module_variant: variant,
      })
    }
  }, [
    variant,
    headerLabel,
    titleEmphasis,
    titleRemainder,
    subtitle,
    shouldDisplay,
    primaryCta,
    secondaryCta,
  ])

  const setAnalyticsEventOnClick = useCallback(
    (ctaType: 'primary' | 'secondary', cta: SanityCta, link: NormalizedSanityLinkTagged) => {
      setAnalyticsEvent({
        event: 'select_content',
        content_type: 'hero_cta',
        item_id: `hero-${variant}-${ctaType}-cta`,
        title: cta.title || '',
        label_text: `${cta.title || ''} (${ctaType} CTA)`,
        link_destination: link._tag === 'Normalized' ? link.href ?? undefined : link._tag,
        module_name: 'SanityHero',
        module_variant: variant,
      })
    },
    [variant],
  )

  const richContent = useMemo(
    () => (content ? (markdownProcessor.processSync(content).result as JSX.Element) : undefined),
    [content],
  )

  const imageElement = useMemo(() => {
    const assetId = heroImage?.asset?._id
    if (!assetId) {
      return undefined
    }
    try {
      const imageData = getGatsbyImageData(
        { _ref: `image-${assetId.replace('image-', '')}` },
        { width: 1200 },
        clientConfig.sanity,
      )

      return (
        imageData && (
          <GatsbyImage
            class={variant === 'hero' ? 'absolute w-full h-full object-cover' : 'h-full'}
            className={variant === 'hero' ? 'absolute w-full h-full object-cover' : ''}
            image={imageData}
            alt={imageAlt || ''}
          />
        )
      )
    } catch (err) {
      console.error('Error creating Gatsby image:', err)

      return heroImage?.asset?.url ? (
        <img
          className={variant === 'hero' ? 'w-full h-full object-cover' : ''}
          src={heroImage.asset.url}
          alt={imageAlt || ''}
        />
      ) : null
    }
  }, [heroImage, imageAlt, variant])

  if (!shouldDisplay) {
    return null
  }

  const primaryCtaButton = primaryCta?.link &&
    primaryCta.link.target &&
    primaryCta.link.target.length > 0 && (
      <SanityLink
        to={primaryCta.link}
        onClick={(_e, normalized) => setAnalyticsEventOnClick('primary', primaryCta, normalized)}
      >
        {variant === 'E' ? (
          <WideRightArrow className="text-brand-white md:w-[25px] md:h-[7px] xl:w-[43px] xl:h-3" />
        ) : (
          primaryCta.title
        )}
      </SanityLink>
    )

  const secondaryCtaButton = secondaryCta?.link &&
    secondaryCta.link.target &&
    secondaryCta.link.target.length > 0 && (
      <SanityLink
        to={secondaryCta.link}
        onClick={(_e, normalized) =>
          setAnalyticsEventOnClick('secondary', secondaryCta, normalized)
        }
      >
        {secondaryCta.title}
      </SanityLink>
    )

  return (
    <Hero
      variant={variant}
      headerLabel={headerLabel}
      titleEmphasis={titleEmphasis}
      titleRemainder={titleRemainder}
      subtitle={subtitle}
      contentNode={richContent}
      primaryCtaButton={primaryCtaButton}
      secondaryCtaButton={secondaryCtaButton}
      heroImage={imageElement}
      imageAlt={imageAlt}
      primaryCtaAriaLabel={primaryCtaAriaLabel}
      secondaryCtaAriaLabel={secondaryCtaAriaLabel}
      mediaType={mediaType}
      mediaUrl={mediaUrl}
      renderPageLink={renderPageLink}
      renderProductCard={renderProductCard}
      renderResourceLink={renderResourceLink}
      productLinks={productLinks?.map((link: ProductLinkType, index: number) => (
        <HeroProductLink key={index} link={link} index={index} />
      ))}
      SearchController={HeroSearchInputController}
    />
  )
}

export default SanityHero

export const sanityHeroFragment = graphql`
  fragment SanityHeroFragment on SanityHero {
    variant
    headerLabel
    titleEmphasis
    titleRemainder
    subtitle
    content
    heroImage {
      asset {
        _id
        url
      }
      hotspot {
        x
        y
        height
        width
      }
      crop {
        top
        bottom
        left
        right
      }
    }
    imageAlt
    primaryCta {
      _type
      title
      link {
        ...LinkFragment
      }
    }
    primaryCtaAriaLabel
    secondaryCta {
      _type
      title
      link {
        ...LinkFragment
      }
    }
    secondaryCtaAriaLabel
    mediaType
    mediaUrl
    productLinks {
      _key
      title
      category
      image {
        asset {
          _id
          url
        }
      }
    }
    roleBasedContent {
      enabled
      roles
      ...RoleBasedContentFragment
    }
    contextBasedContent {
      ...ContextBasedContentFragment
    }
  }
`
